export { b as AUTH_STORAGE_KEYS, y as AnalyticsManager, x as ApiKeyManager, l as AuthApi, r as AuthManager, o as AuthService, a as AuthTokenManager, K as BookingManager, B as BusinessManager, C as CampaignManager, J as CustomFieldDefinitionManager, h as DEFAULT_PERS_CONFIG, d as DPOP_STORAGE_KEYS, p as DPoPManager, D as DefaultAuthProvider, z as DonationManager, F as FATAL_AUTH_CODES, w as FileManager, I as IndexedDBTokenStorage, L as LocalStorageTokenStorage, M as MemoryTokenStorage, g as PersApiClient, q as PersEventEmitter, P as PersSDK, v as PurchaseManager, R as RedemptionManager, S as SDK_NAME, f as SDK_USER_AGENT, e as SDK_VERSION, k as StaticJwtAuthProvider, t as TokenManager, T as TrackingManager, u as TransactionManager, E as TriggerSourceManager, U as UserManager, s as UserStatusManager, H as WalletEventsManager, W as WebDPoPCryptoProvider, G as WebhookManager, i as buildApiRoot, j as buildWalletEventsWsUrl, c as createPersSDK, n as isFatalAuthErrorInMessage, m as mergeWithDefaults } from './chunks/pers-sdk-BEF_pjl0.js';
export { AccountOwnerType, DATA_SOURCE_CHANNELS, DEFAULT_DATA_SOURCE, KNOWN_SOURCE_DOMAINS, detectSourceFromReferrer, isDataSourceChannel, parseUtmParams } from '@explorins/pers-shared';
export { c as ApiErrorDetector, A as AuthenticationError, a as CacheService, C as CacheTTL, E as ErrorUtils, L as LogoutRequired, N as NetworkError, b as PersApiError, P as ProviderTokenRefreshNeeded, S as SdkErrorCodes, T as TokenRefreshNeeded, g as globalCacheService } from './chunks/index--OssIds0.js';
export { T as TenantManager } from './chunks/tenant-manager-xmYKBFGu.js';
import './user.js';
import './chunks/pagination-utils-9vQ-Npkr.js';
import './user-status.js';
import './chunks/token-service-Ba9fp95X.js';
import './chunks/business-membership-service-C2CouLTR.js';
import './campaign.js';
import './chunks/redemption-service-CQ73aUTe.js';
import './chunks/transaction-request.builder-D6VLhaMi.js';
import './chunks/payment-service-IvM6rryM.js';
import './chunks/analytics-service-CF7hSwhy.js';
import './donation.js';
import './trigger-source.js';
import './chunks/tenant-service-DlhdZAs9.js';

/**
 * Environment Detection Utility
 * Detects runtime environment for platform-specific behaviors
 */
/**
 * Detects the current runtime environment
 */
function detectEnvironment() {
    const global = globalThis;
    const isNode = typeof process !== 'undefined' &&
        process.versions?.node != null &&
        typeof global.window === 'undefined';
    const isBrowser = typeof global.window !== 'undefined' &&
        typeof global.document !== 'undefined';
    const isReactNative = typeof global.navigator?.product === 'string' &&
        global.navigator.product.toLowerCase() === 'reactnative';
    const isWeb = isBrowser || isReactNative;
    const needsNodeStreamPolyfills = isWeb;
    return {
        isNode,
        isBrowser,
        isReactNative,
        isWeb,
        needsNodeStreamPolyfills
    };
}
/**
 * Global environment info (cached)
 */
const environment = detectEnvironment();
/**
 * Warns if environment might have bundling issues
 */
function warnIfProblematicEnvironment(feature) {
    if (environment.needsNodeStreamPolyfills) {
        console.warn(`[PERS SDK] ${feature} may require additional polyfills in web environments. ` +
            `If you encounter 'readable-stream' errors, ensure proper polyfills are configured.`);
    }
}

/**
 * DataSource Tracking - SDK Extensions
 *
 * Re-exports from @explorins/pers-shared with SDK-specific utilities.
 *
 * Most types and utilities come from pers-shared:
 * - DataSource, DataSourceChannel
 * - Platform, UtmParams, WebDataSourceConfig, NativeDataSourceConfig
 * - parseUtmParams(), detectSourceFromReferrer(), KNOWN_SOURCE_DOMAINS
 *
 * SDK adds:
 * - detectWebPlatform() - Browser User-Agent detection (requires DOM)
 *
 * @module @explorins/pers-sdk/data-source
 */
/**
 * Detect web platform from User-Agent (browser environments only)
 *
 * This uses `navigator.userAgent` which is only available in browsers.
 * For Node.js or React Native, use platform-specific detection.
 *
 * @example
 * ```typescript
 * const platform = detectWebPlatform();
 * sdk.setPlatform(platform);
 * // { os: 'macOS', browser: 'Chrome', browserVersion: '125.0', deviceType: 'desktop' }
 * ```
 */
function detectWebPlatform() {
    if (typeof navigator === 'undefined') {
        return {};
    }
    const ua = navigator.userAgent;
    const platform = {};
    if (!ua)
        return platform;
    // Device type detection
    if (/mobile|android|iphone|ipod/i.test(ua)) {
        platform.deviceType = 'phone';
    }
    else if (/ipad|tablet/i.test(ua)) {
        platform.deviceType = 'tablet';
    }
    else {
        platform.deviceType = 'desktop';
    }
    // OS detection
    if (/windows/i.test(ua)) {
        platform.os = 'Windows';
        const match = ua.match(/Windows NT ([\d.]+)/);
        if (match)
            platform.osVersion = match[1];
    }
    else if (/macintosh|mac os/i.test(ua) && !/iphone|ipad|ipod/i.test(ua)) {
        platform.os = 'macOS';
        const match = ua.match(/Mac OS X ([\d_.]+)/);
        if (match)
            platform.osVersion = match[1].replace(/_/g, '.');
    }
    else if (/linux/i.test(ua) && !/android/i.test(ua)) {
        platform.os = 'Linux';
    }
    else if (/iphone|ipad|ipod/i.test(ua)) {
        platform.os = 'iOS';
        const match = ua.match(/OS ([\d_]+)/);
        if (match)
            platform.osVersion = match[1].replace(/_/g, '.');
    }
    else if (/android/i.test(ua)) {
        platform.os = 'Android';
        const match = ua.match(/Android ([\d.]+)/);
        if (match)
            platform.osVersion = match[1];
    }
    // Browser detection
    if (/edg\//i.test(ua)) {
        platform.browser = 'Edge';
        const match = ua.match(/Edg\/([\d.]+)/);
        if (match)
            platform.browserVersion = match[1];
    }
    else if (/chrome/i.test(ua) && !/edg/i.test(ua)) {
        platform.browser = 'Chrome';
        const match = ua.match(/Chrome\/([\d.]+)/);
        if (match)
            platform.browserVersion = match[1];
    }
    else if (/safari/i.test(ua) && !/chrome/i.test(ua)) {
        platform.browser = 'Safari';
        const match = ua.match(/Version\/([\d.]+)/);
        if (match)
            platform.browserVersion = match[1];
    }
    else if (/firefox/i.test(ua)) {
        platform.browser = 'Firefox';
        const match = ua.match(/Firefox\/([\d.]+)/);
        if (match)
            platform.browserVersion = match[1];
    }
    return platform;
}

export { detectEnvironment, detectWebPlatform, environment, warnIfProblematicEnvironment };
//# sourceMappingURL=core.js.map
